import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  FRONT_MUSCLES, BACK_MUSCLES,
  FRONT_VIEWBOX, BACK_VIEWBOX
} from '../lib/musclePathData';
import { displayMuscleName } from '../lib/muscleUtils';
import { translateMuscle } from '../lib/muscleTranslations';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

/*
 * Anatomical Muscle Body Map — MuscleMap library integration
 * Features: heat coloring, gradient fills, pulsing glow, click-to-select, tooltips
 * https://github.com/melihcolpan/MuscleMap (MIT License)
 */

// Heat color scale — relative to max sets in the workout
const HEAT_COLORS = [
  '#525252', // 0 — inactive
  '#7a4a12', // ~15% — barely worked
  '#b86800', // ~30%
  '#d97706', // ~45%
  '#ea6c08', // ~60%
  '#f95d00', // ~75%
  '#ff3d00', // ~90%
  '#ff1a00', // 100% — most worked
];

function getHeatColor(sets, maxSets) {
  if (!sets || !maxSets) return HEAT_COLORS[0];
  // Linear scale: gives clear contrast between few sets (3) and many sets (12+)
  const ratio = sets / maxSets;
  const idx = Math.min(7, Math.max(1, Math.ceil(ratio * 7)));
  return HEAT_COLORS[idx];
}

// HSL → hex (continuous scale helper)
function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const col = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(col * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Absolute color scale for daily workouts — continuous yellow→red lerp
// 0–2 sets: inactive grey; 3 sets: warm yellow; 16+ sets: pure red
function getDailyHeatColor(sets) {
  if (!sets || sets < 3) return '#525252'; // inactive (0–2 sets)
  const t = Math.min(1, (sets - 3) / (16 - 3));
  const hue = 50 - t * 50; // 50° yellow at 3 sets → 0° red at 16+ sets
  return hslToHex(hue, 85, 48);
}

function darkenHex(hex, factor) {
  const h = hex.replace('#', '').slice(0, 6);
  const r = Math.round(parseInt(h.slice(0, 2), 16) * factor).toString(16).padStart(2, '0');
  const g = Math.round(parseInt(h.slice(2, 4), 16) * factor).toString(16).padStart(2, '0');
  const b = Math.round(parseInt(h.slice(4, 6), 16) * factor).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

function getGradientStops(sets, maxSets, colorMode) {
  if (!sets || sets <= 0) return null;
  const base = colorMode === 'daily' ? getDailyHeatColor(sets) : getHeatColor(sets, maxSets);
  // Always use opaque hex for gradient stops — alpha in base causes 3-colour bleed in light mode
  const opaqueBase = base.slice(0, 7);
  let dim;
  if (colorMode === 'daily') {
    // Softer darkening — radial gradient fades to this colour at the muscle edges,
    // so a dramatic dim would create a vignette that still makes small muscles look dim.
    dim = darkenHex(opaqueBase, 0.72);
  } else {
    dim = getHeatColor(Math.max(0, sets * 0.5), maxSets);
  }
  return { from: dim, to: opaqueBase };
}

const BODY_COLOR = '#525252';
const GAP_COLOR = '#1a1a1a';
const HAIR_COLOR = '#2a2a2a';

// Normalize viewBox: make both views render at same visual height
// Front: 0 95 727 1280, Back: 860 95 600 1300
// We'll render both in equal-size containers with preserveAspectRatio
const FRONT_VB = '0 95 727 1280';
const BACK_VB = '775 85 700 1320';

/* ======================================================================
   CSS keyframes for pulse animation (injected once)
   ====================================================================== */
const PULSE_STYLE = `
@keyframes muscleGlow {
  0%, 100% { filter: drop-shadow(0 0 3px rgba(255,80,0,0.2)); }
  50% { filter: drop-shadow(0 0 8px rgba(255,80,0,0.6)); }
}
@keyframes musclePulse {
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
}
`;

let styleInjected = false;
function injectStyles() {
  if (styleInjected) return;
  const el = document.createElement('style');
  el.textContent = PULSE_STYLE;
  document.head.appendChild(el);
  styleInjected = true;
}

/* ======================================================================
   Single Body View (front or back)
   ====================================================================== */
export function BodyView({
  muscles, viewBox, stats, maxSets, label,
  interactive = false, selectedMuscle, onSelect, onHover,
  showGradients = false, showPulse = false, compact = false,
  colorMode = 'relative',
}) {
  // SVG gradient IDs are page-global — React.useId() guarantees each rendered
  // BodyView gets its own namespace. Without this, multiple posts on the same
  // feed resolve `url(#grad-front-abs)` to whichever post defined it first,
  // so a high-sets user's red gradient would paint over a low-sets user's
  // golden gradient. (See bug caught in v3.8.85 debugging.)
  const instanceId = React.useId();
  const gradientId = `${instanceId}-${label.toLowerCase()}`;
  const isDark = document.documentElement.classList.contains('dark');
  const bodyColor = isDark ? BODY_COLOR : '#b0b0b0';
  const gapColor = isDark ? GAP_COLOR : '#9a9a9a';

  const resolveColor = (sets) =>
    colorMode === 'daily' ? getDailyHeatColor(sets) : getHeatColor(sets, maxSets);

  // statKey may be a string or an array: when one SVG region covers several
  // target muscles (e.g. upperBack paints 'upper back' + 'lats' + 'rhomboids'),
  // we sum across keys. Single-string statKeys stay identical.
  const groupSets = (group) => {
    if (!group.statKey) return 0;
    if (Array.isArray(group.statKey)) {
      return group.statKey.reduce((acc, k) => acc + (stats[k] || 0), 0);
    }
    return stats[group.statKey] || 0;
  };
  const primaryKey = (group) =>
    Array.isArray(group.statKey) ? group.statKey[0] : group.statKey;

  return (
    <div className="relative" data-testid={`body-view-${label.toLowerCase()}`}>
      <svg
        viewBox={viewBox}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        <defs>
          {/* Gradient definitions for active muscles */}
          {showGradients && muscles.filter(g => g.statKey && groupSets(g) > 0).map((group) => {
            const sets = groupSets(group);
            const stops = getGradientStops(sets, maxSets, colorMode);
            if (!stops) return null;
            return (
              <radialGradient
                key={`grad-${gradientId}-${group.slug}`}
                id={`grad-${gradientId}-${group.slug}`}
                cx="0.5" cy="0.5" r="0.55"
              >
                {/* Centred spotlight per muscle: bright core, dimmer edges.
                    Symmetric shape means small and large muscles both show the
                    base colour in their core — no top-dark/bottom-bright
                    asymmetry that made small muscles read as dimmer. */}
                <stop offset="35%" stopColor={stops.to} />
                <stop offset="100%" stopColor={stops.from} />
              </radialGradient>
            );
          })}
        </defs>

        {muscles.map((group) => {
          const isBody = group.statKey === null;
          const isHair = group.slug === 'hair';
          const sets = isBody ? 0 : groupSets(group);
          const groupKey = primaryKey(group);
          const isSelected = !isBody && selectedMuscle === groupKey;
          const isActive = !isBody && sets > 0;
          const useGradient = showGradients && isActive;

          let fill;
          if (isBody) {
            fill = isHair ? HAIR_COLOR : bodyColor;
          } else if (useGradient) {
            fill = `url(#grad-${gradientId}-${group.slug})`;
          } else {
            fill = resolveColor(sets);
          }

          const ratio = maxSets > 0 ? sets / maxSets : 0;
          // Glow halo (drop-shadow) and opacity pulse are disabled for daily mode
          // because they distort perceived colour on small muscles: a 4px orange
          // halo around a narrow path (triceps, forearms) blends with the fill
          // and makes the muscle read much lighter than its true base colour.
          const shouldPulse = showPulse && colorMode !== 'daily' && ratio > 0.75;
          const shouldGlow = showPulse && colorMode !== 'daily' && ratio > 0.45;

          return group.paths.map((d, i) => (
            <path
              key={`${group.slug}-${i}`}
              d={d}
              fill={fill}
              stroke={isSelected ? (isDark ? '#fff' : '#000') : (isBody ? 'none' : gapColor)}
              strokeWidth={isSelected ? '3' : (isBody ? '0' : '0.5')}
              strokeLinejoin="round"
              style={{
                cursor: interactive && !isBody ? 'pointer' : 'default',
                pointerEvents: isBody ? 'none' : 'all',
                transition: 'fill 0.4s ease, stroke 0.3s ease, stroke-width 0.2s ease',
                animation: shouldPulse
                  ? 'musclePulse 2s ease-in-out infinite'
                  : 'none',
                filter: isSelected
                  ? (isDark ? 'drop-shadow(0 0 6px rgba(255,255,255,0.5))' : 'drop-shadow(0 0 8px rgba(0,0,0,0.4))')
                  : shouldGlow
                    ? 'drop-shadow(0 0 4px rgba(255,80,0,0.35))'
                    : 'none',
              }}
              onClick={(e) => {
                if (interactive && !isBody) {
                  e.stopPropagation();
                  onSelect?.(groupKey === selectedMuscle ? null : groupKey);
                }
              }}
              onMouseEnter={() => {
                if (!isBody) onHover?.({ muscle: groupKey, slug: group.slug, sets });
              }}
              onMouseLeave={() => {
                if (!isBody) onHover?.(null);
              }}
            />
          ));
        })}
      </svg>
      {label && !compact && (
        <div className="text-center mt-0.5">
          <span className="text-[8px] font-bold tracking-[2px] uppercase text-neutral-500"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   Heat Legend
   ====================================================================== */
function HeatLegend({ compact }) {
  const { t } = useTranslation();
  const levels = [
    { label: t('muscleMap.heatLegend0'), color: '#3a3a3a' },
    { label: t('muscleMap.heatLegendLow'), color: '#b86800' },
    { label: t('muscleMap.heatLegendMed'), color: '#ea6c08' },
    { label: t('muscleMap.heatLegendHigh'), color: '#ff3d00' },
    { label: t('muscleMap.heatLegendMax'), color: '#ff1a00' },
  ];
  return (
    <div className={`flex items-center justify-center gap-1 ${compact ? 'mt-0.5' : 'mt-2'}`} data-testid="heat-legend">
      <span className="text-[10px] text-muted-foreground mr-0.5 uppercase tracking-wider font-semibold">{t('muscleMap.sets')}</span>
      {levels.map(l => (
        <div key={l.label} className="flex flex-col items-center">
          <div className={`${compact ? 'w-3 h-1' : 'w-5 h-1.5'} rounded-sm`} style={{ backgroundColor: l.color }} />
          <span className={`${compact ? 'text-[10px]' : 'text-[10px]'} text-muted-foreground leading-tight mt-px`}>{l.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ======================================================================
   Compact inline thumbnail — click to open detail
   Shows small front+back next to badges
   ====================================================================== */
export function MuscleBodyMapCompact({ muscleStats = {}, onClick, colorMode = 'relative' }) {
  const { t } = useTranslation();
  const hasStats = Object.values(muscleStats).some(v => v > 0);
  const maxSets = useMemo(() => Math.max(0, ...Object.values(muscleStats)), [muscleStats]);
  if (!hasStats) return null;

  return (
    <div
      className="flex items-center gap-0.5 cursor-pointer group"
      onClick={onClick}
      data-testid="muscle-body-map-compact"
      title="Click to view muscle analysis"
    >
      <div className="w-[40px] flex-shrink-0 transition-transform group-hover:scale-105 pointer-events-none">
        <BodyView muscles={FRONT_MUSCLES} viewBox={FRONT_VB} stats={muscleStats} maxSets={maxSets} label={t('muscleMap.front')} compact colorMode={colorMode} />
      </div>
      <div className="w-[36px] flex-shrink-0 transition-transform group-hover:scale-105 pointer-events-none">
        <BodyView muscles={BACK_MUSCLES} viewBox={BACK_VB} stats={muscleStats} maxSets={maxSets} label={t('muscleMap.back')} compact colorMode={colorMode} />
      </div>
    </div>
  );
}

/* ======================================================================
   Large — front+back side by side, bigger than the compact pair.
   Fills the horizontal width of the routine-board volume rail while
   staying short vertically, so the per-muscle badges below stay visible
   on one screen (no scroll). ~3x the size of MuscleBodyMapCompact.
   ====================================================================== */
export function MuscleBodyMapLarge({ muscleStats = {}, onClick, colorMode = 'relative' }) {
  const { t } = useTranslation();
  const hasStats = Object.values(muscleStats).some(v => v > 0);
  const maxSets = useMemo(() => Math.max(0, ...Object.values(muscleStats)), [muscleStats]);
  if (!hasStats) return null;

  return (
    <div
      className="flex items-start justify-center gap-1 w-full cursor-pointer group"
      onClick={onClick}
      data-testid="muscle-body-map-large"
      title="Click to view muscle analysis"
    >
      <div className="w-1/2 max-w-[130px] flex-shrink-0 transition-transform group-hover:scale-105 pointer-events-none">
        <BodyView muscles={FRONT_MUSCLES} viewBox={FRONT_VB} stats={muscleStats} maxSets={maxSets} label={t('muscleMap.front')} compact colorMode={colorMode} />
      </div>
      <div className="w-1/2 max-w-[120px] flex-shrink-0 transition-transform group-hover:scale-105 pointer-events-none">
        <BodyView muscles={BACK_MUSCLES} viewBox={BACK_VB} stats={muscleStats} maxSets={maxSets} label={t('muscleMap.back')} compact colorMode={colorMode} />
      </div>
    </div>
  );
}

/* ======================================================================
   Detail Modal — Full interactive muscle map
   ====================================================================== */
export function MuscleDetailModal({ muscleStats = {}, isOpen, onClose, routineName, colorMode = 'relative' }) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [selectedMuscle, setSelectedMuscle] = useState(null);
  const [hovered, setHovered] = useState(null);

  // Inject CSS animations
  React.useEffect(() => { injectStyles(); }, []);

  const maxSets = useMemo(() => Math.max(0, ...Object.values(muscleStats)), [muscleStats]);

  // Sort muscles by sets for the stats list
  const sortedMuscles = useMemo(() => {
    return Object.entries(muscleStats)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a);
  }, [muscleStats]);

  const displayInfo = selectedMuscle
    ? { muscle: selectedMuscle, sets: muscleStats[selectedMuscle] || 0 }
    : hovered;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85dvh] flex flex-col p-0 gap-0" data-testid="muscle-detail-modal">
        <DialogHeader className="px-5 py-3 border-b border-border/40 flex-shrink-0">
          <DialogTitle className="text-sm font-bold">{t('muscleMap.muscleAnalysisTitle')}</DialogTitle>
          {routineName && <DialogDescription className="text-xs">{routineName}</DialogDescription>}
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Muscle map */}
          <div className="flex items-start justify-center gap-4 mb-3">
            <div className="w-full max-w-[175px] flex-shrink-0">
              <BodyView
                muscles={FRONT_MUSCLES}
                viewBox={FRONT_VB}
                stats={muscleStats}
                maxSets={maxSets}
                label={t('muscleMap.front')}
                interactive
                selectedMuscle={selectedMuscle}
                onSelect={setSelectedMuscle}
                onHover={setHovered}
                showGradients
                showPulse
                colorMode={colorMode}
              />
            </div>
            <div className="w-full max-w-[160px] flex-shrink-0">
              <BodyView
                muscles={BACK_MUSCLES}
                viewBox={BACK_VB}
                stats={muscleStats}
                maxSets={maxSets}
                label={t('muscleMap.back')}
                interactive
                selectedMuscle={selectedMuscle}
                onSelect={setSelectedMuscle}
                onHover={setHovered}
                showGradients
                showPulse
                colorMode={colorMode}
              />
            </div>
          </div>

          {/* Tooltip / selected info */}
          <div className="h-5 text-center mb-2">
            {displayInfo && displayInfo.sets > 0 ? (
              <span className="text-xs animate-in fade-in duration-200">
                <span className="font-bold text-primary">{displayMuscleName(displayInfo.muscle, language)}</span>
                <span className="text-muted-foreground ml-1.5">{t('muscleMap.setsPerWeek', { count: displayInfo.sets })}</span>
              </span>
            ) : (
              <span className="text-xs text-muted-foreground/60">{t('muscleMap.clickOrHoverHint')}</span>
            )}
          </div>

          <HeatLegend />

          {/* Balance bars + stats */}
          <div className="mt-4 pt-4 border-t border-border/40">
            <BalanceBarsInline muscleStats={muscleStats} />
          </div>

          {/* Muscle breakdown list */}
          <div className="mt-4 pt-3 border-t border-border/40">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('muscleMap.muscleBreakdown')}
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {sortedMuscles.map(([muscle, sets]) => (
                <div
                  key={muscle}
                  className={`flex items-center gap-2 py-0.5 px-1.5 rounded cursor-pointer transition-colors ${
                    selectedMuscle === muscle ? 'bg-primary/10' : 'hover:bg-muted/40'
                  }`}
                  onClick={() => setSelectedMuscle(selectedMuscle === muscle ? null : muscle)}
                >
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: colorMode === 'daily' ? getDailyHeatColor(sets) : getHeatColor(sets, maxSets) }} />
                  <span className="text-xs flex-1 truncate">{displayMuscleName(muscle, language)}</span>
                  <span className="text-xs text-muted-foreground font-mono">{sets}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}/* ======================================================================
   Balance Bars (inline version for the modal)
   ====================================================================== */
function BalanceBarsInline({ muscleStats }) {
  const upperMuscles = ['pectorals', 'delts', 'rear deltoids', 'upper back', 'lats', 'rhomboids', 'traps', 'biceps', 'triceps', 'forearms'];
  const lowerMuscles = ['quads', 'hamstrings', 'glutes', 'calves', 'adductors', 'abductors'];
  const pushMuscles = ['pectorals', 'delts', 'triceps', 'quads', 'calves'];
  const pullMuscles = ['upper back', 'lats', 'rhomboids', 'rear deltoids', 'traps', 'biceps', 'forearms', 'hamstrings', 'glutes'];

  const sum = (keys) => keys.reduce((acc, k) => acc + (muscleStats[k] || 0), 0);
  const upper = sum(upperMuscles);
  const lower = sum(lowerMuscles);
  const push = sum(pushMuscles);
  const pull = sum(pullMuscles);
  const total = upper + lower;
  const ppTotal = push + pull;

  const upperPct = total > 0 ? Math.round((upper / total) * 100) : 50;
  const pushPct = ppTotal > 0 ? Math.round((push / ppTotal) * 100) : 50;

  return (
    <div className="space-y-3">
      {/* Upper/Lower */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Upper / Lower</span>
          <span className="text-xs text-muted-foreground">{upper} / {lower} sets</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden flex bg-muted/30">
          <div className="h-full rounded-l-full transition-all duration-500"
            style={{ width: `${upperPct}%`, background: 'linear-gradient(90deg, #3b82f6, #6366f1)' }} />
          <div className="h-full rounded-r-full transition-all duration-500"
            style={{ width: `${100 - upperPct}%`, background: 'linear-gradient(90deg, #10b981, #34d399)' }} />
        </div>
        <div className="flex justify-between mt-0.5">
          <span className="text-[9px] font-medium text-blue-400">Upper {upperPct}%</span>
          <span className="text-[9px] font-medium text-emerald-400">Lower {100 - upperPct}%</span>
        </div>
      </div>
      {/* Push/Pull */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Push / Pull</span>
          <span className="text-xs text-muted-foreground">{push} / {pull} sets</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden flex bg-muted/30">
          <div className="h-full rounded-l-full transition-all duration-500"
            style={{ width: `${pushPct}%`, background: 'linear-gradient(90deg, #ef4444, #f97316)' }} />
          <div className="h-full rounded-r-full transition-all duration-500"
            style={{ width: `${100 - pushPct}%`, background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }} />
        </div>
        <div className="flex justify-between mt-0.5">
          <span className="text-[9px] font-medium text-red-400">Push {pushPct}%</span>
          <span className="text-[9px] font-medium text-purple-400">Pull {100 - pushPct}%</span>
        </div>
      </div>
      {/* Core */}
      <div className="text-[9px] text-muted-foreground">
        Core: {['abs', 'obliques', 'serratus anterior', 'lower back', 'core'].reduce((acc, k) => acc + (muscleStats[k] || 0), 0)} sets
      </div>
    </div>
  );
}

/* ======================================================================
   Default export — Full size (kept for edit mode / backward compat)
   ====================================================================== */
export default function MuscleBodyMap({ muscleStats = {}, size = 'md', interactive = false, selectedMuscle: controlledSelected, onSelect, hideExtras = false, colorMode = 'relative' }) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);
  const [internalSelected, setInternalSelected] = useState(null);
  const selectedMuscle = controlledSelected !== undefined ? controlledSelected : internalSelected;
  const handleSelect = onSelect || setInternalSelected;
  const maxSets = useMemo(() => Math.max(0, ...Object.values(muscleStats)), [muscleStats]);
  const sizeClass = size === 'xs' ? 'w-full max-w-[108px]' : size === 'sm' ? 'w-full max-w-[130px]' : size === 'lg' ? 'w-full max-w-[210px]' : 'w-full max-w-[170px]';
  const backSize = size === 'xs' ? 'max-w-[98px]' : size === 'sm' ? 'max-w-[120px]' : size === 'lg' ? 'max-w-[190px]' : 'max-w-[155px]';

  React.useEffect(() => { injectStyles(); }, []);

  const displayInfo = selectedMuscle
    ? { muscle: selectedMuscle, sets: muscleStats[selectedMuscle] || 0 }
    : hovered;

  return (
    <div className="w-full" data-testid="muscle-body-map" onClick={interactive ? (e) => e.stopPropagation() : undefined}>
      <div className="flex items-start justify-center gap-2">
        <div className={`${sizeClass} flex-shrink-0`}>
          <BodyView muscles={FRONT_MUSCLES} viewBox={FRONT_VB} stats={muscleStats} maxSets={maxSets}
            label={t('muscleMap.front')} interactive={interactive} selectedMuscle={selectedMuscle} onSelect={handleSelect} onHover={setHovered} showGradients showPulse compact={size === 'sm' || size === 'xs'} colorMode={colorMode} />
        </div>
        <div className={`${backSize} flex-shrink-0`}>
          <BodyView muscles={BACK_MUSCLES} viewBox={BACK_VB} stats={muscleStats} maxSets={maxSets}
            label={t('muscleMap.back')} interactive={interactive} selectedMuscle={selectedMuscle} onSelect={handleSelect} onHover={setHovered} showGradients showPulse compact={size === 'sm' || size === 'xs'} colorMode={colorMode} />
        </div>
      </div>
      {!hideExtras && displayInfo && displayInfo.sets > 0 ? (
        <div className="text-center mt-1 animate-in fade-in duration-200">
          <span className="text-xs font-bold capitalize text-primary">{translateMuscle(displayInfo.muscle, language)}</span>
          <span className="text-xs text-muted-foreground ml-1.5">{t('muscleMap.sets', { count: displayInfo.sets })}</span>
        </div>
      ) : !hideExtras && hovered && hovered.sets > 0 ? (
        <div className="text-center mt-1 animate-in fade-in duration-200">
          <span className="text-xs font-bold capitalize text-primary">{translateMuscle(hovered.muscle, language)}</span>
          <span className="text-xs text-muted-foreground ml-1.5">{t('muscleMap.sets', { count: hovered.sets })}</span>
        </div>
      ) : null}
      {!hideExtras && <HeatLegend compact={size === 'sm'} />}
    </div>
  );
}
